import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { Cell, Label, LabelList, Pie, PieChart } from 'recharts';
import { CategoricalChartProps } from 'recharts/types/chart/generateCategoricalChart';
import { PolarViewBox } from 'recharts/types/util/types';

import { CustomChartBase, InjectedStyledComponent } from '../../../shared/index.js';
import Legend from '../../base/Legend/Legend.js';
import { ILegendItemProps, ILegendProps } from '../../base/Legend/models/index.js';
import { ITranslationContextValue } from '../../base/Translate/models/index.js';
import { TranslationContext } from '../../base/Translate/Translate.js';

import {
	IGaugeBoundData,
	IGaugeCalculateValuePosOnCircleOptions,
	IGaugeChartConfiguration,
	IGaugeChartProps,
	IGaugeChartState,
	IGaugeNeedleActiveShapeProps,
	IGaugeNeedleCircleProps,
	IGaugeNeedleProps,
	IGaugeRelativeBound,
	TGaugeCategoryKey,
	TGaugeCategoryKeyWithMax,
} from './models/index.js';
import ChartWrapper from './styledComponents/ChartWrapper.js';
import Wrapper from './styledComponents/Wrapper.js';
import { GAUGE_CHART_CATEGORY_KEY, GAUGE_CHART_COMPONENT_KEY } from './GaugeChart.meta.js';

const RADIAN = (Math.PI * 2) / 100;

export class GaugeChart extends CustomChartBase<IGaugeChartProps, IGaugeChartState, IGaugeChartConfiguration> {
	categoryKey = GAUGE_CHART_CATEGORY_KEY;
	componentKey = GAUGE_CHART_COMPONENT_KEY;
	translationContext: ITranslationContextValue | undefined;
	currentNeedleIndex = 0;

	componentDidUpdate() {
		this.currentNeedleIndex = 0;
	}

	get width() {
		const { dimensions } = this.props;
		if (dimensions && dimensions.width) {
			return dimensions.width;
		}
		console.warn('Gauge Chart width not set');
	}

	get height() {
		const { dimensions } = this.props;
		if (dimensions && dimensions.height) {
			return dimensions.height;
		}
		console.warn('Gauge Chart height not set');
	}

	get maxRadiusPercentage() {
		const { dimensions } = this.props;
		if (dimensions && dimensions.maxRadiusPercentage) {
			return dimensions.maxRadiusPercentage;
		}
		return 0.8;
	}

	get diameter() {
		let diameter = 500;
		if (!this.width && this.height) {
			diameter = this.height;
		} else if (!this.height && this.width) {
			diameter = this.width;
		} else if (this.width && this.height) {
			diameter = this.width <= this.height ? this.width : this.height;
		}
		return diameter;
	}

	get radius() {
		return this.diameter / 2;
	}

	get boundThickness() {
		const { bounds } = this.props;
		return bounds && bounds.thickness ? bounds.thickness : 25;
	}

	get hideBoundLabels() {
		const { bounds } = this.props;
		return (bounds && bounds.hideLabels) || false;
	}

	get sortedBounds() {
		const { bounds } = this.props;
		if (bounds && this.boundsMax) {
			const { categories } = bounds;
			if (categories) {
				return [
					{ key: 'outstanding' as TGaugeCategoryKey, color: 'darkgreen', ...categories.outstanding },
					{ key: 'good' as TGaugeCategoryKey, color: 'lightgreen', ...categories.good },
					{ key: 'moderate' as TGaugeCategoryKey, color: 'orange', ...categories.moderate },
					{ key: 'poor' as TGaugeCategoryKey, color: 'darkred', ...categories.poor },
				].sort((a, b) => a.min - b.min);
			}
		}
		return [];
	}

	get bounds(): IGaugeBoundData[] {
		// tslint:disable-next-line: no-this-assignment
		const { boundsMax } = this;
		const { bounds } = this.props;
		if (bounds && boundsMax) {
			const { categories } = bounds;
			if (categories) {
				return this.sortedBounds.map((category, index, categoryList) => {
					const nextIndex = index + 1;
					const categoryMax = nextIndex < categoryList.length ? categoryList[nextIndex].min : boundsMax;
					return {
						key: category.key,
						value: 90,
						color: category.color,
						label: `${category.label || category.min}`,
						range: {
							min: category.min,
							max: categoryMax,
						},
					};
				});
			}
			console.warn('Gauge Chart bound categories not set');
		}
		return [];
	}

	get boundsMaxLabel() {
		const { bounds } = this.props;
		if (bounds) {
			return bounds.maxLabel;
		}
	}

	get boundsMax() {
		const { bounds } = this.props;
		if (bounds && bounds.maxValue !== undefined) {
			return bounds.maxValue;
		}
		console.warn('Gauge Chart max value not set');
	}

	get boundsMin() {
		const boundCategories = this.sortedBounds;
		if (boundCategories && boundCategories.length > 0) {
			return boundCategories[0].min;
		}
	}

	get pieChartProps(): CategoricalChartProps {
		return {
			width: this.width,
			height: this.height,
		};
	}

	get basePieProps() {
		const outerRadius = this.radius * this.maxRadiusPercentage;
		return {
			outerRadius,
			innerRadius: outerRadius - this.boundThickness,
			dataKey: 'value',
			isAnimationActive: false,
			startAngle: 90,
			endAngle: -270,
			cx: this.radius,
			cy: this.radius,
		};
	}

	// PieProps are incompatible with Pie due to:
	// Type 'LegacyRef<SVGElement> | undefined' is not assignable to type 'LegacyRef<Pie> | undefined'.
	get pieProps(): any {
		return Object.assign({}, this.basePieProps, {
			data: this.bounds,
			fill: '#8884d8',
		});
	}

	// PieProps are incompatible with Pie due to:
	// Type 'LegacyRef<SVGElement> | undefined' is not assignable to type 'LegacyRef<Pie> | undefined'.
	get needleProps(): any {
		return Object.assign({}, this.basePieProps, {
			activeIndex: 0,
			fill: 'none',
			stroke: 'none',
			activeShape: this.NeedleActiveShape,
		});
	}

	get needles(): IGaugeNeedleProps[] | undefined {
		const { needles } = this.props;
		if (needles) {
			return needles;
		}
	}

	get primaryNeedle(): IGaugeNeedleProps | undefined {
		return this.needles && this.needles.find((needle) => needle.primary === true);
	}

	constructor(props: IGaugeChartProps) {
		super(props);

		makeObservable(this, {
			width: computed,
			height: computed,
			maxRadiusPercentage: computed,
			diameter: computed,
			radius: computed,
			boundThickness: computed,
			hideBoundLabels: computed,
			sortedBounds: computed,
			bounds: computed,
			boundsMaxLabel: computed,
			boundsMax: computed,
			boundsMin: computed,
			pieChartProps: computed,
			basePieProps: computed,
			pieProps: computed,
			needleProps: computed,
			needles: computed,
			primaryNeedle: computed,
		});
	}

	getLegendItems = (): ILegendProps['items'] => {
		let items: ILegendProps['items'] = [];
		const needles = this.needles;
		if (needles) {
			items = needles.map((needle) => {
				const item: ILegendItemProps = {
					color: {
						color: needle.color,
					},
					label: {
						value: needle.label || '',
					},
				};
				if (typeof needle.value === 'number') {
					item.value = {
						value: needle.value.toString(),
					};
				}
				return item;
			});
		}
		return items;
	};

	getRelativeBound(value: number): IGaugeRelativeBound | undefined {
		// tslint:disable-next-line: no-this-assignment
		const { bounds, boundsMin, boundsMax } = this;
		if (bounds) {
			if (boundsMin && value <= boundsMin) {
				return { bound: bounds[0], index: 0 };
			}

			if (boundsMax && value >= boundsMax) {
				const lastIndex = bounds.length - 1;
				return { bound: bounds[lastIndex], index: lastIndex };
			}

			for (let i = 0; i < bounds.length; i++) {
				const bound = bounds[i];
				const min = bound.range.min;
				const max = bound.range.max;
				const inRange = min <= value && value < max;
				if (inRange) {
					return { bound, index: i };
				}
			}
		}
	}

	getNeedlePercent(value: number) {
		// tslint:disable-next-line: no-this-assignment
		const { boundsMin, boundsMax } = this;
		const relativeBound = this.getRelativeBound(value);
		if (relativeBound) {
			// Cap value at min & max.
			if (boundsMin && value < boundsMin) {
				value = boundsMin;
			} else if (boundsMax && value > boundsMax) {
				value = boundsMax;
			}

			// determine needle position by determining the range percent + previous quadrants.
			const { bound, index } = relativeBound;
			const min = bound.range.min;
			const max = bound.range.max;
			const val = value - min;
			const valPercent = val / (max - min);
			const portionOfQuad = 25 * valPercent;
			const previousQuadsValue = 25 * index;
			return portionOfQuad + previousQuadsValue;
		}
	}

	getNeedleData(needle: IGaugeNeedleProps) {
		const { value } = needle;
		const percent = this.getNeedlePercent(value);
		if (percent !== undefined) {
			const inversePercent = 100 - percent;
			return [{ value: percent }, { value: inversePercent }];
		}
	}

	parseAsNumber(value: string | number | undefined): number | undefined {
		if (typeof value === 'string') {
			return parseInt(value, 10);
		}
		return value;
	}

	getNeedleCircleProps(
		needle: IGaugeNeedleProps,
		key: 'inner' | 'outer',
		defaults: { offset?: number; color?: string },
	) {
		const circle: IGaugeNeedleCircleProps | undefined = needle.circles && needle.circles[key];

		return Object.assign(
			{},
			{
				enabled: (circle && circle.radius && circle.radius !== 0) || false,
				color: defaults.color,
				borderColor: defaults.color,
				backgroundColor: 'white',
				offset: defaults.offset,
			},
			circle,
		);
	}

	getCurrentNeedleDisplayProps() {
		const { needles } = this.props;
		if (needles && needles[this.currentNeedleIndex]) {
			const needle: IGaugeNeedleProps = needles[this.currentNeedleIndex];

			this.currentNeedleIndex++;
			if (this.currentNeedleIndex >= needles.length) {
				this.currentNeedleIndex = 0;
			}

			const color = needle.color || '#000000';

			return {
				color,
				label: needle.label,
				thickness: common.commonUtils.safe(() => needle.thickness, 0),
				inner: this.getNeedleCircleProps(needle, 'inner', {
					color,
					offset: (needle.offset && needle.offset.inner) || 70,
				}),
				outer: this.getNeedleCircleProps(needle, 'outer', {
					color,
					offset: (needle.offset && needle.offset.outer) || 0,
				}),
			};
		}
	}

	calculateValuePositionOnCircle(value: number, options: IGaugeCalculateValuePosOnCircleOptions) {
		const { cx, cy, radius, radiusOffset } = options;

		const center = {
			x: this.parseAsNumber(cx) || 0,
			y: this.parseAsNumber(cy) || 0,
		};
		const distance = {
			radius: this.parseAsNumber(radius) || 0,
			offset: this.parseAsNumber(radiusOffset) || 0,
		};

		const sin = Math.sin(RADIAN * (value - 25));
		const cos = Math.cos(RADIAN * (value - 25));

		return {
			x: center.x + (distance.radius + distance.offset) * cos,
			y: center.y + (distance.radius + distance.offset) * sin,
		};
	}

	translate(text: string) {
		if (this.translationContext) {
			return this.translationContext.translate(text);
		}
		return text;
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	// Module '"recharts/types/polar/Pie"' declares 'PieActiveShape' locally, but it is not exported.
	NeedleActiveShape = (props: IGaugeNeedleActiveShapeProps): any => {
		const { cx, cy, outerRadius, value } = props;

		const display = this.getCurrentNeedleDisplayProps();

		if (!display) {
			return;
		}

		const inner = this.calculateValuePositionOnCircle(value, {
			cx,
			cy,
			radiusOffset: display.inner.offset,
		});
		const outer = this.calculateValuePositionOnCircle(value, {
			cx,
			cy,
			radius: outerRadius,
			radiusOffset: display.outer.offset,
		});

		return (
			<g>
				<path
					d={`M${inner.x},${inner.y}L${outer.x},${outer.y}`}
					strokeWidth={display.thickness}
					stroke={display.color}
					fill="none"
					strokeLinecap="round"
				/>
				{display.outer.enabled && (
					<circle
						cx={outer.x}
						cy={outer.y}
						r={display.outer.radius}
						fill={display.outer.backgroundColor}
						stroke={display.outer.borderColor || 'none'}
						strokeWidth={display.outer.borderWidth}
					/>
				)}
				{display.outer.enabled && display.label && (
					<text
						fill={display.outer.color}
						stroke={display.outer.color}
						textAnchor="middle"
						dominantBaseline="middle"
						x={outer.x}
						y={outer.y}
					>
						{this.translate(display.label)}
					</text>
				)}
				{display.inner.enabled && (
					<circle
						cx={inner.x}
						cy={inner.y}
						r={display.inner.radius}
						fill={display.inner.backgroundColor}
						stroke={display.inner.borderColor || 'none'}
						strokeWidth={display.inner.borderWidth}
					/>
				)}
				{display.inner.enabled && display.label && (
					<text
						fill={display.inner.color}
						stroke={display.inner.color}
						textAnchor="middle"
						dominantBaseline="middle"
						x={inner.x}
						y={inner.y}
					>
						{this.translate(display.label)}
					</text>
				)}
			</g>
		);
	};

	getCellLabelAttributes(value: number, key: TGaugeCategoryKeyWithMax, viewBox: PolarViewBox) {
		const { cx, cy, innerRadius, outerRadius } = viewBox as PolarViewBox;

		let attributesOverrides;
		let initialPlacementOverride;
		let radiusOffsetOverride;
		let paddingOverride;
		const { bounds } = this.props;
		if (bounds) {
			const { textAttributes } = bounds;
			const { radiusOffset, padding, initialPlacement, ...attributes } =
				(textAttributes && textAttributes[key]) || {};
			radiusOffsetOverride = radiusOffset;
			paddingOverride = padding;
			initialPlacementOverride = initialPlacement;
			attributesOverrides = attributes;
		}
		const initialPlacement = initialPlacementOverride || (key === 'max' ? 'inner' : 'outer');
		const defaultRadiusOffset = initialPlacement === 'inner' ? -8 : 8;
		const radiusOffset = radiusOffsetOverride === undefined ? defaultRadiusOffset : radiusOffsetOverride;
		const padding = Object.assign({ x: 0, y: 0 }, paddingOverride);

		const pos = this.calculateValuePositionOnCircle(value, {
			cx,
			cy,
			radiusOffset,
			radius: initialPlacement === 'inner' ? innerRadius : outerRadius,
		});

		let textAnchor;
		switch (value) {
			case 0:
				textAnchor = 'start';
				break;
			case 25:
				textAnchor = initialPlacement === 'inner' ? 'end' : 'start';
				break;
			case 50:
			case 100:
				textAnchor = 'end';
				break;
			case 75:
				textAnchor = initialPlacement === 'inner' ? 'start' : 'end';
				break;
			default:
				textAnchor = 'middle';
				break;
		}

		let dominantBaseline;
		switch (value) {
			case 0:
			case 100:
				dominantBaseline = initialPlacement === 'inner' ? 'hanging' : 'auto';
				break;
			case 75:
				dominantBaseline = 'auto';
				break;
			case 25:
				dominantBaseline = 'hanging';
				break;
			case 50:
				dominantBaseline = initialPlacement === 'inner' ? 'auto' : 'hanging';
				break;
			default:
				dominantBaseline = 'middle';
				break;
		}

		return Object.assign(
			{},
			{
				textAnchor,
				dominantBaseline,
			},
			{
				x: pos.x + padding.x,
				y: pos.y + padding.y,
				...attributesOverrides,
			},
		);
	}

	getCellLabelDisplayProps(viewBox: PolarViewBox, index: number) {
		const bound = this.bounds[index];
		const value = index * 25;
		const current = this.getCellLabelAttributes(value, bound.key, viewBox);

		let last;
		if (value === 75) {
			last = this.getCellLabelAttributes(100, 'max', viewBox);
		}

		return {
			current,
			last,
			bound,
		};
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	CellLabel = (props: any) => {
		const { viewBox, index } = props;
		const display = this.getCellLabelDisplayProps(viewBox, index);

		return (
			<>
				<text {...display.current}>{display.bound.label || display.bound.range.min}</text>
				{display.last && <text {...display.last}>{this.boundsMaxLabel || display.bound.range.max}</text>}
			</>
		);
	};

	getCenterLabelDisplayProps(viewBox: PolarViewBox) {
		const { centerLabel: options } = this.props;
		const defaultPadding = options && options.textAttributes && options.textAttributes.padding;
		const { padding: topPadding, ...topAttributes } =
			(options && options.textAttributes && options.textAttributes.top) || {};
		const { padding: bottomPadding, ...bottomAttributes } =
			(options && options.textAttributes && options.textAttributes.bottom) || {};
		const padding = {
			top: Object.assign({ x: 0, y: -5 }, defaultPadding, topPadding),
			bottom: Object.assign({ x: 0, y: 5 }, defaultPadding, bottomPadding),
		};
		const center = {
			x: viewBox.cx || 0,
			y: viewBox.cy || 0,
		};

		return {
			attributes: {
				top: Object.assign({ fontSize: 30 }, topAttributes),
				bottom: Object.assign({ fontSize: 20 }, bottomAttributes),
			},
			position: {
				top: {
					x: center.x + padding.top.x,
					y: center.y + padding.top.y,
				},
				bottom: {
					x: center.x + padding.bottom.x,
					y: center.y + padding.bottom.y,
				},
			},
			labelPos: (options && options.description && options.description.position) || 'bottom',
			label: (options && options.description && options.description.label) || '',
			value:
				(options && options.description && options.description.value) ||
				(this.primaryNeedle && this.primaryNeedle.value),
		};
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	CenterLabel = (props: any) => {
		const display = this.getCenterLabelDisplayProps(props.viewBox);

		if (!display.value && !display.label) {
			return null;
		}

		return (
			<>
				<text
					textAnchor="middle"
					dominantBaseline="auto"
					{...display.attributes.top}
					x={display.position.top.x}
					y={display.position.top.y}
				>
					{display.labelPos === 'top' ? this.translate(display.label) : display.value}
				</text>
				<text
					textAnchor="middle"
					dominantBaseline="hanging"
					{...display.attributes.bottom}
					x={display.position.bottom.x}
					y={display.position.bottom.y}
				>
					{display.labelPos === 'bottom' ? this.translate(display.label) : display.value}
				</text>
			</>
		);
	};

	render() {
		const { className } = this.props;
		const legendProps = this.legendProps;
		const legendPosition = this.legendPosition;
		return (
			<Wrapper className={className}>
				{legendProps && legendPosition === 'top' && <Legend {...legendProps} />}
				<ChartWrapper>
					<TranslationContext.Consumer>
						{(translationContext) => {
							this.translationContext = translationContext;
							return (
								<PieChart {...this.pieChartProps}>
									<Pie {...this.pieProps}>
										{this.bounds.map((entry, index) => (
											<Cell
												key={`cell-${index}`}
												fill={this.bounds[index].color}
												strokeWidth={2}
											/>
										))}
										{!this.hideBoundLabels && (
											<LabelList dataKey="color" content={this.CellLabel} />
										)}
										<Label position="center" content={this.CenterLabel} />
									</Pie>
									{this.needles &&
										this.needles.map((needle, needleIndex) => {
											return (
												<Pie
													key={`needle-${needleIndex}`}
													{...this.needleProps}
													data={this.getNeedleData(needle)}
												></Pie>
											);
										})}
								</PieChart>
							);
						}}
					</TranslationContext.Consumer>
				</ChartWrapper>
				{legendProps && legendPosition === 'bottom' && <Legend {...legendProps} />}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(GaugeChart), GAUGE_CHART_CATEGORY_KEY, GAUGE_CHART_COMPONENT_KEY);
